import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Lock } from 'lucide-react'
import { ViewSwitch } from '@/components/ui/view-switch'
import { StoreOfferings } from '@/components/features/storefront/StoreOfferings'
import { AccessNote, CARD_GRID_GAPS, PlanScroller, SubscriptionValuePanel } from '@/components/features/storefront/StorefrontSections'
import { comboPlansFor, offerOptionAt, offerTotal, planBenefitLines } from '@/components/features/storefront/storefrontDisplay'
import { SuspendedNotice } from '@/components/features/memberEntry/SuspendedNotice'
import { OfferPlanCard } from '@/components/features/spaces/OfferPlanCard'
import { SignInInvite } from '@/components/features/spaces/SignInInvite'
import { useRoute } from '@/lib/RouteContext'
import { useSubscription } from '@/lib/SubscriptionContext'
import { changePlanPath, pricingOfferPath } from '@/lib/checkout/pricingPath'
import { loadSubscriptionGroups } from '@/lib/checkout/subscriptionGroupsCache'
import { openPlanCheckout, prefetchPlanCheckout } from '@/lib/checkout/planCheckoutNav'
import { yearlyToggleSavings } from '@/lib/checkout/annual'
import { sdk } from '@/lib/sdk'
import { cn } from '@/lib/utils'
import type { AccessOffers } from '@/types/access'
import type { StorefrontAddOn } from '@/types/storefront'
import type { PublicProduct, PublicSubscriptionGroup } from '@/types/checkout'
import { reloadPage } from '@/lib/player/leave'

/** The GA list the offer's cards are reported in (`item_list_id`). */
const OFFER_LIST = 'space_offer'

interface SpaceOfferSaleProps {
  /** The space's name, as the menu shows it. */
  space: string
  /** Why it is closed ("Exige um plano"), the small line above the headline. */
  reasonLabel?: string | null
  offers: AccessOffers
  signedIn: boolean
  onSignIn: () => void
}

/** One plan card of the offer: the plan, its options on sale and the add-ons it can carry. */
interface PlanEntry {
  plan: PublicSubscriptionGroup
  options: PublicProduct[]
  /** The add-on it needs to open the space; `null` when the plan opens it by itself. */
  addOn: StorefrontAddOn | null
  benefits: string[]
}

/** A plan's lines from the public plan list: its benefits, else its description, one per line. */
const groupBenefitLines = (group: PublicSubscriptionGroup): string[] =>
  group.metadata?.benefits?.length
    ? group.metadata.benefits
    : (group.description ?? '').split('\n').map((line) => line.replace(/^[-•*]\s*/, '').trim()).filter(Boolean)

/**
 * The sale on a padlocked space (David, 2026-10-30: "essa tela é a chance de
 * vender"), aligned to the start, never a centered stack.
 *
 * Without a plan (a visitor, or a member who has none), every plan that
 * opens it is shown alike, side by side: the plans that open it by
 * themselves and those that open it with an add-on, the add-on inside the
 * card (ticked and locked when needed, optional otherwise). One plan: its
 * headline and benefits on the left, the card on the right.
 *
 * With a plan, the offer starts from it: the add-on to add to it when it
 * takes one (`add`), else the plans to switch to (the plan change of
 * billing; an add-on still needed is added after the change).
 *
 * One-off products that open it stay as product cards, in the same block.
 */
export function SpaceOfferSale({ space, reasonLabel = null, offers, signedIn, onSignIn }: SpaceOfferSaleProps) {
  const { t, i18n } = useTranslation(['common', 'checkout'])
  const { navigate } = useRoute()
  const { subscription } = useSubscription()
  const { plans: offerPlans, addOns, products } = offers

  /* The public plan list has every plan's options with each add-on's price on them (F-37). */
  const needsGroups = offerPlans.length > 0 || addOns.length > 0
  const [groups, setGroups] = useState<PublicSubscriptionGroup[] | null>(null)
  useEffect(() => {
    if (!needsGroups) return
    let alive = true
    /* The shared list (the plan's checkout opens on it with no wait); the checkout's code comes along. */
    prefetchPlanCheckout()
    Promise.resolve(loadSubscriptionGroups(sdk.getCommunityId()))
      .then((list) => { if (alive) setGroups(Array.isArray(list) ? list : []) })
      .catch(() => { if (alive) setGroups([]) })
    return () => { alive = false }
  }, [needsGroups])

  /* Whether the person has a plan: their subscription, or what the API says of the add-ons for them. */
  const hasPlan = signedIn && (Boolean(subscription) || addOns.some((addOn) => addOn.availability !== 'subscribe'))
  const currentGroupId = subscription
    ? (groups ?? []).find((group) => group.products.some((option) => option.id === subscription.productId))?.id ?? null
    : null
  /* With a plan that takes the add-on, the add-on is the whole offer. */
  const addable = hasPlan ? addOns.filter((addOn) => addOn.availability === 'add') : []

  /*
   * The plan cards: the plans that open it alone (their offered options), then the plans that open it with
   * an add-on (the options that price it), in the plans' own order. A plan that opens it alone still
   * offers the add-on as an option.
   */
  const entries: PlanEntry[] = []
  if (groups && addable.length === 0) {
    const openingAddOns = addOns.filter((addOn) => addOn.availability !== 'owned')
    const carriers = new Map(openingAddOns.map((addOn) => [addOn.id, comboPlansFor(addOn, groups).map((group) => group.id)]))
    for (const group of groups) {
      if (hasPlan && group.id === currentGroupId) continue
      const offered = offerPlans.find((plan) => plan.id === group.id)
      const carried = openingAddOns.filter((addOn) => carriers.get(addOn.id)?.includes(group.id))
      if (!offered && carried.length === 0) continue
      const options = offered
        ? group.products.filter((option) => offered.options.some((o) => o.id === option.id))
        : group.products.filter((option) => carried.some((addOn) => option.addOnAmounts?.some((amount) => amount.productId === addOn.id)))
      if (options.length === 0) continue
      entries.push({
        plan: group,
        options,
        /* A plan that opens it alone offers no add-on here (David, 2026-10-30); otherwise the first it carries. */
        addOn: offered ? null : carried[0],
        benefits: offered ? planBenefitLines(offered) : groupBenefitLines(group)
      })
    }
  }

  /* Where the plan's checkout returns once paid: this space, now open. */
  const back = window.location.pathname

  /* One billing toggle for the plan cards, when they bill at both rhythms. Monthly first. */
  const intervals = ['MONTHLY', 'YEARLY'].filter((i) => entries.some((entry) => entry.options.some((o) => o.billingInterval === i)))
  /* Opens on the cheaper rhythm (David, 2026-10-30): yearly when it saves, else monthly. The viewer's pick wins. */
  const [billing, setBilling] = useState('')
  const cheaper = yearlyToggleSavings(entries.map((entry) => entry.plan.products)) ? 'YEARLY' : 'MONTHLY'
  const interval = intervals.length > 1 ? (intervals.includes(billing) ? billing : intervals.includes(cheaper) ? cheaper : intervals[0]) : null
  /* Cheapest first, by what each card charges at the chosen interval (plan plus the add-on it needs);
     ties keep the plans' own order (David, 2026-10-30). The Store keeps the team's order. */
  const perMonth = (entry: PlanEntry) => {
    const option = offerOptionAt(entry.options, interval)
    return offerTotal(entry.options, entry.addOn?.id ?? null, interval) / (option?.billingInterval === 'YEARLY' ? 12 : 1)
  }
  /* Per month, so a plan with no yearly option (shown monthly) compares fairly with the yearly ones. */
  entries.sort((x, y) => perMonth(x) - perMonth(y))
  const billingSwitch = interval ? (
    <ViewSwitch
      value={interval}
      onChange={setBilling}
      aria-label={t('common:storefront.billingLabel')}
      data-testid="space-offer-billing"
      className="w-auto flex-shrink-0"
      options={intervals.map((i) => (() => {
          const saving = i === 'YEARLY' ? yearlyToggleSavings(entries.map((entry) => entry.plan.products)) : null
          return {
            value: i,
            label: t(i === 'YEARLY' ? 'checkout:yearly' : 'checkout:monthly'),
            badge: saving ? t(saving.upTo ? 'common:storefront.savingsUpTo' : 'common:storefront.savingsTag', { percent: saving.percent }) : null
          }
        })())}
    />
  ) : null

  const accessNote = <AccessNote />

  const subscribe = (entry: PlanEntry) => (option: PublicProduct, addOnIds: string[]) => {
    if (entry.plan.slug) void openPlanCheckout({ navigate, path: pricingOfferPath(entry.plan.slug, option.optionKey, { addOnIds, back }) })
  }
  const card = (entry: PlanEntry, single: boolean) => (
    <OfferPlanCard
      key={entry.plan.id}
      plan={entry.plan}
      options={entry.options}
      interval={interval}
      addOn={entry.addOn}
      reserveAddOnRow={!single && entries.some((other) => other.addOn)}
      aligned={!single}
      benefits={entry.benefits}
      showTotal={!single || Boolean(entry.addOn)}
      recommended={!single && entry.options.some((option) => option.isHighlighted)}
      action={hasPlan ? 'change' : 'subscribe'}
      footer={hasPlan ? null : <div className="mt-2 text-center">{accessNote}</div>}
      onSubscribe={subscribe(entry)}
      onChangePlan={(option, addOnIds) => entry.plan.slug && navigate(changePlanPath(entry.plan.slug, option.optionKey, { addOnIds, back }))}
    />
  )

  /* The headline names what opens it; the line under it says what the person gets. */
  const list = (names: string[]) => {
    try {
      /* `Intl.ListFormat` is not in this project's TypeScript lib yet; every supported browser has it. */
      const { ListFormat } = Intl as unknown as { ListFormat: new (locale: string, options: { type: 'conjunction' }) => { format: (items: string[]) => string } }
      return new ListFormat(i18n.language, { type: 'conjunction' }).format(names)
    } catch {
      return names.join(', ')
    }
  }
  const single = entries.length === 1 ? entries[0] : null
  let title: string
  let value: string
  if (addable.length > 0) {
    title = t('common:access.offer.titleAddOn', { addOn: list(addable.map((addOn) => addOn.title)) })
    value = t('common:access.offer.valueAddOn', { addOn: list(addable.map((addOn) => addOn.title)), space })
  } else if (hasPlan && entries.length > 0) {
    title = t('common:access.offer.titleUpgrade')
    value = t('common:access.offer.valueUpgrade', { space })
  } else if (single) {
    title = single.addOn
      ? t('common:access.offer.titleWith', { plan: single.plan.name, addOn: single.addOn.title })
      : t('common:access.offer.titleOnePlan', { plan: single.plan.name })
    value = t('common:access.offer.valueOnePlan', { plan: single.plan.name, space })
  } else if (entries.length > 0 && products.length > 0) {
    /* More than one way in: one choice, not everything at once. The space's name is in the header. */
    title = t('common:access.offer.titlePlanOrProduct')
    value = t('common:access.offer.valueOneOf')
  } else if (entries.length > 0) {
    title = t('common:access.offer.titlePlans')
    value = t('common:access.offer.valuePlans')
  } else if (products.length > 0 && addOns.length === 0) {
    title = t('common:access.offer.titleProducts')
    value = t('common:access.offer.valueProducts')
  } else {
    title = t('common:access.offer.titleMixed')
    value = t('common:access.offer.valueOneOf')
  }

  const header = (
    <div className="min-w-0">
      {reasonLabel && (
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary-700">
          <Lock className="h-3.5 w-3.5" aria-hidden="true" />
          {reasonLabel}
        </p>
      )}
      <h2 id="space-offer-title" className={cn('text-balance text-2xl font-bold text-gray-900 sm:text-3xl', reasonLabel && 'mt-2')} data-testid="space-offer-heading">
        {title}
      </h2>
      <p className="mt-2 max-w-2xl text-gray-600">{value}</p>
      {!signedIn && <SignInInvite onSignIn={onSignIn} className="mt-4" />}
    </div>
  )

  /* The other ways in, in the same block: one-off products, and add-ons for the plan the person has. */
  const extras = addable.length > 0 || products.length > 0 || (entries.length === 0 && addOns.length > 0) ? (
    <StoreOfferings
      plans={[]}
      addOns={addable.length > 0 ? addable : entries.length === 0 ? addOns : []}
      products={products}
      listId={OFFER_LIST}
      explainer={false}
      bare
      plain
      planLink={(slug, optionKey) => pricingOfferPath(slug, optionKey, { back })}
      onPurchased={() => reloadPage()}
    />
  ) : null

  let body: ReactNode
  if (needsGroups && groups === null) {
    body = (
      <div className="flex justify-center py-16" data-testid="space-offer-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-hidden="true" />
      </div>
    )
  } else if (single && !extras) {
    /* One plan: the headline on the left, the same plan card as the other cases on the right, with the
       billing toggle above it (where the other cases have it: the block's header, on the right). */
    body = (
      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_24rem] lg:gap-12" data-testid="space-offer-split">
        <div className="min-w-0">
          {header}
          <SubscriptionValuePanel className="mt-10 border-t border-primary-100 pt-8" />
        </div>
        <div className="w-full sm:mx-auto sm:max-w-sm lg:max-w-none">
          {billingSwitch && (
            <div className="mb-4 flex items-center gap-2 lg:justify-end">
              <span className="text-xs text-gray-500">{t('common:storefront.billingLabel')}</span>
              {billingSwitch}
            </div>
          )}
          {card(single, true)}
        </div>
      </div>
    )
  } else {
    const count = entries.length
    const recommendedPad = entries.some((entry) => entry.options.some((option) => option.isHighlighted))
    const grid = count === 0 ? null
      : count >= 5 ? <PlanScroller count={count}>{entries.map((entry) => card(entry, false))}</PlanScroller>
        : (
          <div
            className={cn(
              CARD_GRID_GAPS,
              'grid grid-cols-1',
              recommendedPad && 'pt-3',
              /* The block's full width, flush with the headline and the billing toggle. */
              count === 1 && 'mx-auto max-w-sm',
              count === 2 && 'md:grid-cols-2',
              count === 3 && 'md:grid-cols-3',
              count === 4 && 'sm:grid-cols-2 min-[1400px]:grid-cols-4'
            )}
          >
            {entries.map((entry) => card(entry, false))}
          </div>
        )
    body = (
      <>
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          {header}
          {billingSwitch && (
            <div className="flex flex-shrink-0 items-center gap-2">
              <span className="text-xs text-gray-500">{t('common:storefront.billingLabel')}</span>
              {billingSwitch}
            </div>
          )}
        </div>
        {grid && extras ? (
          /* Two ways in: each with its label, and "ou" between them. */
          <div data-testid="space-offer-grid">
            <p className="mb-4 text-sm font-semibold text-gray-900">{t('common:access.offer.groupPlans')}</p>
            {grid}
            <div className="my-10 flex items-center gap-4" aria-hidden="true">
              <span className="h-px flex-1 bg-primary-200" />
              <span className="text-sm font-semibold uppercase tracking-wide text-primary-700">{t('common:access.offer.or')}</span>
              <span className="h-px flex-1 bg-primary-200" />
            </div>
            <p className="mb-4 text-sm font-semibold text-gray-900">
              {t('common:access.offer.groupProducts')}
              <span className="ml-2 font-normal text-gray-500">{t('common:access.offer.groupProductsHint')}</span>
            </p>
            {extras}
          </div>
        ) : (
          <div className="space-y-10" data-testid="space-offer-grid">
            {grid}
            {extras}
          </div>
        )}
      </>
    )
  }

  return (
    <>
      {/* F-86: a suspended community shows what it sells, nothing can be bought. */}
      <SuspendedNotice kind="sale" className="mb-6" />
      {body}
    </>
  )
}
